import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../context/authContext";
import FloatingNavigation from "../components/FloatingNavigation";
import {
  FaUser,
  FaIdCard,
  FaUniversity,
  FaMapMarkerAlt,
  FaCreditCard,
  FaPhone,
  FaEnvelope,
  FaPlus,
  FaSpinner,
  FaCheckCircle
} from "react-icons/fa";

function AddGuidePage() {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    guideName: "",
    guideId: "",
    bankName: "",
    branch: "",
    accountNumber: "",
    phone: "",
    email: "",
  });

  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);

  // Calculate form completion percentage
  const completionPercentage = Math.round(
    (Object.values(formData).filter(value => value.trim() !== "").length / Object.keys(formData).length) * 100
  );

  const handleChange = (e) => {
    let value = e.target.value;
    
    // Format phone number
    if (e.target.name === 'phone') {
      value = value.replace(/\D/g, ''); // Remove non-digits
      if (value.length <= 10) {
        // Format as XXX-XXX-XXXX
        if (value.length > 6) {
          value = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
        } else if (value.length > 3) {
          value = `${value.slice(0, 3)}-${value.slice(3)}`;
        }
      }
    }
    
    // Format account number (remove spaces and special chars except digits)
    if (e.target.name === 'accountNumber') {
      value = value.replace(/\D/g, '');
    }
    
    setFormData({ ...formData, [e.target.name]: value });
    
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const validateForm = () => {
    const formErrors = {};
    
    // Validate required fields
    for (const key in formData) {
      if (!formData[key].trim()) {
        formErrors[key] = "This field is required";
      }
    }

    // Additional validations
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      formErrors.email = "Please enter a valid email address";
    }

    if (formData.phone && !/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      formErrors.phone = "Please enter a valid 10-digit phone number";
    }

    if (formData.accountNumber && formData.accountNumber.length < 8) {
      formErrors.accountNumber = "Account number must be at least 8 digits";
    }

    if (formData.guideId && formData.guideId.length < 3) {
      formErrors.guideId = "Guide ID must be at least 3 characters";
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/add-guide`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSuccessMessage("✅ Guide added successfully!");
      setTimeout(() => setSuccessMessage(""), 5000);
      
      // Notify parent window if running in iframe
      if (window.parent !== window) {
        window.parent.postMessage({ type: 'GUIDE_ADDED', guide: formData }, '*');
      }
      
      // Reset form
      setFormData({
        guideName: "",
        guideId: "",
        bankName: "",
        branch: "",
        accountNumber: "",
        phone: "",
        email: "",
      });
      setErrors({});
      setShowPreview(false);
    } catch (error) {
      console.error("Error adding guide:", error);
      const errorMessage = error.response?.data?.message || "Failed to add guide. Please try again.";
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formFields = [
    { 
      label: "Guide Name", 
      name: "guideName", 
      type: "text", 
      icon: FaUser,
      placeholder: "Enter guide's full name"
    },
    { 
      label: "Guide ID", 
      name: "guideId", 
      type: "text", 
      icon: FaIdCard,
      placeholder: "Enter unique guide ID"
    },
    { 
      label: "Bank Name", 
      name: "bankName", 
      type: "text", 
      icon: FaUniversity,
      placeholder: "Enter bank name"
    },
    { 
      label: "Branch", 
      name: "branch", 
      type: "text", 
      icon: FaMapMarkerAlt,
      placeholder: "Enter branch name/location"
    },
    { 
      label: "Bank Account Number", 
      name: "accountNumber", 
      type: "text", 
      icon: FaCreditCard,
      placeholder: "Enter account number"
    },
    { 
      label: "Phone Number", 
      name: "phone", 
      type: "tel", 
      icon: FaPhone,
      placeholder: "Enter phone number"
    },
    { 
      label: "Email Address", 
      name: "email", 
      type: "email", 
      icon: FaEnvelope,
      placeholder: "Enter email address"
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-3xl mx-auto px-3 sm:px-4 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
              <FaPlus className="mr-2 sm:mr-3 text-blue-600 text-lg sm:text-xl" />
              Add New Guide
            </h1>
            <p className="mt-1 text-xs sm:text-sm text-gray-500">
              Register a new guide with complete information and banking details
            </p>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 sm:mb-6 bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-center">
              <FaCheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mr-2 sm:mr-3 flex-shrink-0" />
              <p className="text-xs sm:text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          {/* Progress Indicator */}
          <div className="mb-4 sm:mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs sm:text-sm font-medium text-gray-700">Form Completion</span>
              <span className="text-xs sm:text-sm text-gray-500">{completionPercentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
              <div 
                className="bg-blue-600 h-1.5 sm:h-2 rounded-full transition-all duration-300"
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* General Error */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-red-800">{errors.submit}</p>
              </div>
            )}

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {formFields.map((field) => {
                const IconComponent = field.icon;
                return (
                  <div key={field.name} className={field.name === 'email' ? 'md:col-span-2' : ''}>
                    <label 
                      htmlFor={field.name}
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2"
                    >
                      {field.label}
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
                        <IconComponent className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                      </div>
                      <input
                        type={field.type}
                        id={field.name}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        className={`block w-full pl-8 sm:pl-10 pr-3 py-2.5 sm:py-3 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 text-xs sm:text-sm ${
                          errors[field.name]
                            ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                        }`}
                        disabled={isSubmitting}
                      />
                    </div>
                    {errors[field.name] && (
                      <p className="mt-1 text-xs sm:text-sm text-red-600">{errors[field.name]}</p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-4 pt-4 sm:pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowPreview(!showPreview)}
                className="px-3 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 order-2 sm:order-1"
                disabled={isSubmitting}
              >
                {showPreview ? "Hide Preview" : "Preview"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormData({
                    guideName: "",
                    guideId: "",
                    bankName: "",
                    branch: "",
                    accountNumber: "",
                    phone: "",
                    email: "",
                  });
                  setErrors({});
                  setSuccessMessage("");
                  setShowPreview(false);
                }}
                className="px-3 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 order-3 sm:order-2"
                disabled={isSubmitting}
              >
                Clear Form
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center px-4 sm:px-6 py-2.5 sm:py-3 border border-transparent text-xs sm:text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-3"
              >
                {isSubmitting ? (
                  <>
                    <FaSpinner className="animate-spin mr-1.5 sm:mr-2 text-xs sm:text-sm" />
                    <span className="text-xs sm:text-sm">Adding Guide...</span>
                  </>
                ) : (
                  <>
                    <FaPlus className="mr-1.5 sm:mr-2 text-xs sm:text-sm" />
                    <span className="text-xs sm:text-sm">Add Guide</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Preview Section */}
        {showPreview && (
          <div className="mt-4 sm:mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
              <FaUser className="mr-1.5 sm:mr-2 text-blue-600 text-sm sm:text-base" />
              <span className="text-sm sm:text-lg">Guide Information Preview</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-500">Guide Name</label>
                  <p className="mt-1 text-xs sm:text-sm text-gray-900">{formData.guideName || "Not provided"}</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-500">Guide ID</label>
                  <p className="mt-1 text-xs sm:text-sm text-gray-900">{formData.guideId || "Not provided"}</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-500">Phone Number</label>
                  <p className="mt-1 text-xs sm:text-sm text-gray-900">{formData.phone || "Not provided"}</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-500">Email Address</label>
                  <p className="mt-1 text-xs sm:text-sm text-gray-900 break-all">{formData.email || "Not provided"}</p>
                </div>
              </div>
              
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-500">Bank Name</label>
                  <p className="mt-1 text-xs sm:text-sm text-gray-900">{formData.bankName || "Not provided"}</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-500">Branch</label>
                  <p className="mt-1 text-xs sm:text-sm text-gray-900">{formData.branch || "Not provided"}</p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-500">Account Number</label>
                  <p className="mt-1 text-xs sm:text-sm text-gray-900">
                    {formData.accountNumber ? `****${formData.accountNumber.slice(-4)}` : "Not provided"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <FloatingNavigation />
    </div>
  );
}

export default AddGuidePage;
